/**
 * DropZone.tsx - Layout Drop Zone Component
 *
 * This component provides drop zones for the layout builder where draggable
 * sections can be placed with visual feedback and zone management.
 *
 * Key Features:
 * - Drop zone areas for layout sections
 * - Visual feedback during drag operations
 * - Zone-specific styling and indicators
 * - Empty state handling with helpful prompts
 * - Zone capacity and validation management
 * - Integration with layout zone system
 *
 * Zone Types:
 * - Top Zone: Full-width header area
 * - Left Zone: Left column with configurable width
 * - Right Zone: Right column with configurable width
 * - Bottom Zone: Full-width footer area
 *
 * Drop Behavior:
 * - Highlight zones during drag operations
 * - Validate drop compatibility
 * - Provide visual feedback for valid/invalid drops
 * - Handle section reordering within zones
 * - Manage zone capacity and restrictions
 *
 * Props:
 * - zoneId: string - Unique identifier for the drop zone
 * - zoneType: 'top' | 'left' | 'right' | 'bottom' - Zone type for styling
 * - children?: React.ReactNode - Existing sections in the zone
 *
 * Dependencies: @dnd-kit/core, MobX store, zone management utilities
 */

import React from "react";
import styled from "styled-components";
import { useDroppable } from "@dnd-kit/core";
import { observer } from "mobx-react-lite";
import { Plus, Grid, Sidebar, Layout } from "lucide-react";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { FundRaiserTemplateLayoutType } from "../../../enums";

// Layout Configuration Constants
const LAYOUT_CONFIGURATIONS = {
  Layout1: {
    topZone: [
      "mission",
      "impact",
      "fundraiserProgress",
      "promoterProgress",
      "stats",
    ],
    leftZone: ["media", "donation"],
    rightZone: ["cta", "promoters", "recent"],
    bottomZone: ["description", "sponsor"],
  },
  Layout2: {
    topZone: [
      "mission",
      "impact",
      "fundraiserProgress",
      "promoterProgress",
      "stats",
    ],
    leftZone: ["cta", "promoters", "recent"],
    rightZone: ["media", "donation"],
    bottomZone: ["description", "sponsor"],
  },
  Layout3: {
    topZone: [
      "mission",
      "impact",
      "fundraiserProgress",
      "promoterProgress",
      "stats",
    ],
    leftZone: ["media", "donation"],
    rightZone: ["cta", "promoters", "recent"],
    bottomZone: ["description", "sponsor"],
  },
  Layout4: {
    topZone: [],
    leftZone: [
      "mission",
      "media",
      "fundraiserProgress",
      "promoterProgress",
      "stats",
      "donation",
    ],
    rightZone: ["impact", "cta", "promoters", "recent"],
    bottomZone: ["description", "sponsor"],
  },
  Layout5: {
    topZone: [
      "mission",
      "impact",
      "media",
      "cta",
      "fundraiserProgress",
      "donation",
      "stats",
      "promoters",
      "recent",
      "description",
      "sponsor",
    ],
    leftZone: [],
    rightZone: [],
    bottomZone: [],
  },
};

/**
 * Places a newly visible section in its designated zone according to layout configuration
 */
export const placeSectionInDesignatedZone = (
  sectionId: string,
  layoutId: string = "Layout1"
) => {
  const config =
    LAYOUT_CONFIGURATIONS[layoutId as keyof typeof LAYOUT_CONFIGURATIONS] ||
    LAYOUT_CONFIGURATIONS.Layout1;

  // Special handling for sponsor section based on banner image
  if (sectionId === "sponsor") {
    const hasBannerImage = !!fundraiserStore.builderData?.bannerImage;
    if (!hasBannerImage) {
      return; // Don't place sponsor section if no banner image
    }

    // Only auto-place sponsor if it's not already in any zone (prevents overriding manual placement)
    const sponsorExistsInAnyZone = Object.values(
      fundraiserStore.layoutZones
    ).some((sections) => sections.includes("sponsor"));

    if (!sponsorExistsInAnyZone) {
      // Place sponsor in bottom zone for layouts 1-4, top zone for layout 5
      const targetZone = layoutId === "Layout5" ? "topZone" : "bottomZone";
      const currentZoneSections = fundraiserStore.layoutZones[targetZone];

      // Add to end of zone if not already there
      if (!currentZoneSections.includes("sponsor")) {
        fundraiserStore.layoutZones[targetZone] = [
          ...currentZoneSections,
          "sponsor",
        ];
      }
    }
    return;
  }

  // Find which zone this section belongs to in the current layout
  let targetZone: keyof typeof fundraiserStore.layoutZones | null = null;

  for (const [zoneKey, sections] of Object.entries(config)) {
    if ((sections as string[]).includes(sectionId)) {
      targetZone = zoneKey as keyof typeof fundraiserStore.layoutZones;
      break;
    }
  }

  // If section has a designated zone, place it there
  if (targetZone) {
    const currentZoneSections = fundraiserStore.layoutZones[targetZone];
    const designatedOrder = config[targetZone];

    if (currentZoneSections.length === 0) {
      // Empty zone - add as first section
      fundraiserStore.layoutZones[targetZone] = [sectionId];
    } else {
      // Zone has existing sections - insert according to predefined order
      const newSections = [...currentZoneSections];

      // Remove section if it already exists (shouldn't happen but safety check)
      const existingIndex = newSections.indexOf(sectionId);
      if (existingIndex !== -1) {
        newSections.splice(existingIndex, 1);
      }

      // Find correct insertion point based on designated order
      let insertIndex = newSections.length; // Default to end

      for (let i = 0; i < designatedOrder.length; i++) {
        const orderSectionId = designatedOrder[i];
        if (orderSectionId === sectionId) {
          // Found our section in the order, now find where to insert
          for (let j = 0; j < newSections.length; j++) {
            const existingSectionId = newSections[j];
            const existingOrderIndex =
              designatedOrder.indexOf(existingSectionId);
            const ourOrderIndex = i;

            if (
              existingOrderIndex > ourOrderIndex ||
              existingOrderIndex === -1
            ) {
              insertIndex = j;
              break;
            }
          }
          break;
        }
      }

      // Only reorder if the section is not already in the zone
      if (!currentZoneSections.includes(sectionId)) {
        newSections.splice(insertIndex, 0, sectionId);
        fundraiserStore.layoutZones[targetZone] = newSections;
      }
    }
  }
};

/**
 * Resets all visible sections to their designated zones according to layout configuration
 */
export const resetSectionsToLayoutDefaults = (layoutId: string = "Layout1") => {
  const config =
    LAYOUT_CONFIGURATIONS[layoutId as keyof typeof LAYOUT_CONFIGURATIONS] ||
    LAYOUT_CONFIGURATIONS.Layout1;
  const sectionVisibility =
    fundraiserStore.builderData?.sectionVisibility || {};

  // Clear all zones first
  Object.keys(fundraiserStore.layoutZones).forEach((zoneKey) => {
    fundraiserStore.layoutZones[
      zoneKey as keyof typeof fundraiserStore.layoutZones
    ] = [];
  });

  // Place all visible sections in their designated zones according to layout order
  for (const [zoneKey, sectionOrder] of Object.entries(config)) {
    const zoneTyped = zoneKey as keyof typeof fundraiserStore.layoutZones;
    const visibleSectionsInOrder = (sectionOrder as string[]).filter(
      (sectionId: string) => {
        // Use explicit visibility check - if not defined, default to true for core sections
        if (sectionVisibility[sectionId] !== undefined) {
          return sectionVisibility[sectionId] !== false;
        }

        // Default visibility for core sections
        const defaultVisibility: Record<string, boolean> = {
          header: true,
          fundraiserProgress: true,
          promoterProgress: true,
          stats: true,
          mission: true,
          impact: true,
          media: true,
          donation: true,
          promoters: true,
          recent: true,
          cta: true,
          description: false,
          sponsor: false,
        };

        return defaultVisibility[sectionId] !== false;
      }
    );

    fundraiserStore.layoutZones[zoneTyped] = visibleSectionsInOrder;
  }

  // Handle sponsor section placement based on banner image
  const hasBannerImage = !!fundraiserStore.builderData?.bannerImage;
  if (hasBannerImage) {
    // Check if sponsor is already placed in any zone (preserve manual placement)
    const sponsorExistsInAnyZone = Object.values(
      fundraiserStore.layoutZones
    ).some((sections) => sections.includes("sponsor"));

    if (!sponsorExistsInAnyZone) {
      const targetZone = layoutId === "Layout5" ? "topZone" : "bottomZone";
      const currentZoneSections = fundraiserStore.layoutZones[targetZone];

      // Add sponsor to end of appropriate zone if not already there
      if (!currentZoneSections.includes("sponsor")) {
        fundraiserStore.layoutZones[targetZone] = [
          ...currentZoneSections,
          "sponsor",
        ];
      }
    }
  } else {
    // Remove sponsor from all zones if no banner image
    Object.keys(fundraiserStore.layoutZones).forEach((zoneKey) => {
      const zone =
        fundraiserStore.layoutZones[
          zoneKey as keyof typeof fundraiserStore.layoutZones
        ];
      const index = zone.indexOf("sponsor");
      if (index !== -1) {
        zone.splice(index, 1);
      }
    });
  }
};

/**
 * Handles section visibility changes - places newly visible sections in appropriate zones
 */
export const handleSectionVisibilityChange = (
  sectionId: string,
  isVisible: boolean,
  layoutId: string = "Layout1"
) => {
  if (isVisible) {
    // Section became visible - place it in designated zone
    placeSectionInDesignatedZone(sectionId, layoutId);
  } else {
    // Section became hidden - remove it from all zones
    Object.keys(fundraiserStore.layoutZones).forEach((zoneKey) => {
      const zone =
        fundraiserStore.layoutZones[
          zoneKey as keyof typeof fundraiserStore.layoutZones
        ];
      const index = zone.indexOf(sectionId);
      if (index !== -1) {
        zone.splice(index, 1);
      }
    });
  }
};

/**
 * Handles layout changes - reorganizes all sections according to new layout
 */
export const handleLayoutChange = (newLayoutId: string) => {
  // Store the current layout in builder data
  fundraiserStore.updateBuilderField("selectedLayout", newLayoutId);

  // Reset sections to new layout defaults
  resetSectionsToLayoutDefaults(newLayoutId);
};

const DropZoneContainer = styled.div<{
  isOver: boolean;
  isEmpty: boolean;
  slotType: string;
  zoneWidth?: number;
  isLayout5?: boolean;
  isLayout4?: boolean;
  layoutId?: string;
  slotId?: string;
}>`
  min-height: ${(props) => {
    if (props.isEmpty) {
      // Layout 5 top zone (single column layout) - special case
      if (props.isLayout5 && props.slotType === "full-width") {
        return "750px";
      }
      // Top, Left, and Right zones should be 400px when empty
      if (
        props.slotType === "left-column" ||
        props.slotType === "right-column"
      ) {
        return "400px";
      }
      // Top zone should be 400px when empty - identify by slotId
      if (props.slotType === "full-width") {
        // Use slotId to determine if this is top or bottom zone
        const isTopZone = props.slotId === "topZone";
        return isTopZone ? "400px" : "100px";
      }
      // Default fallback
      return "100px";
    }
    return "40px";
  }};
  width: 100%;
  border: 2px dashed ${(props) => (props.isOver ? "#3b82f6" : "#cbd5e1")};
  border-radius: 12px;
  background: ${(props) =>
    props.isOver
      ? "rgba(59, 130, 246, 0.15)"
      : props.isEmpty
        ? "rgba(252, 231, 243, 0.8)"
        : "rgba(59, 130, 246, 0.05)"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  transition: all 0.3s ease;
  margin: 0;
  position: relative;

  ${(props) =>
    props.isOver &&
    `
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.2);
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.25);
  `}

  border-style: ${(props) => (props.isEmpty ? "dashed" : "solid")};
  border-width: ${(props) => (props.isEmpty ? "2px" : "1px")};
`;

const DropZoneText = styled.div<{ isOver: boolean }>`
  font-size: 17px;
  font-weight: 600;
  color: ${(props) => (props.isOver ? "#3b82f6" : "#be185d")};
  text-align: center;
  transition: all 0.2s ease;
`;

const ZoneDescription = styled.div<{ isOver: boolean; slotType: string }>`
  font-size: 13px;
  color: ${(props) => (props.isOver ? "#2563eb" : "#be185d")};
  text-align: center;
  max-width: ${(props) =>
    props.slotType === "left-column"
      ? "350px"
      : props.slotType === "right-column"
        ? "280px"
        : "400px"};
  line-height: 1.4;
`;

interface DropZoneProps {
  slotId: string;
  slotType: "full-width" | "left-column" | "right-column";
  isEmpty: boolean;
  layoutId?: string;
}

export const DropZone: React.FC<DropZoneProps> = observer(
  ({ slotId, slotType, isEmpty }) => {
    // Get zone width from current layout
    console.log(slotType, slotId);
    const getZoneWidth = () => {
      const selectedLayoutId =
        fundraiserStore.builderData?.selectedLayout ||
        FundRaiserTemplateLayoutType.Layout1;
      const layoutTemplates: Record<string, Record<string, number>> = {
        Layout1: { topZone: 100, leftZone: 67, rightZone: 33, bottomZone: 100 },
        Layout2: { topZone: 100, leftZone: 33, rightZone: 67, bottomZone: 100 },
        Layout3: { topZone: 100, leftZone: 50, rightZone: 50, bottomZone: 100 },
        Layout4: { topZone: 0, leftZone: 67, rightZone: 33, bottomZone: 100 },
        Layout5: { topZone: 100, leftZone: 0, rightZone: 0, bottomZone: 0 },
      };

      const zoneKey = slotId.replace("-dropzone", "");
      return layoutTemplates[selectedLayoutId]?.[zoneKey] || 100;
    };

    const zoneWidth = getZoneWidth();
    const selectedLayoutId =
      fundraiserStore.builderData?.selectedLayout ||
      FundRaiserTemplateLayoutType.Layout1;
    const isLayout4 = selectedLayoutId === FundRaiserTemplateLayoutType.Layout4;

    const { isOver, setNodeRef } = useDroppable({
      id: `${slotId}-dropzone`,
      data: {
        type: "zone",
        slotId,
        slotType,
      },
    });

    // Don't render if zone width is 0 (hidden) - this ensures proper zone visibility
    if (zoneWidth === 0) {
      return null;
    }

    const getDropZoneText = () => {
      if (isEmpty) {
        switch (slotType) {
          case "full-width":
            return slotId.includes("top")
              ? "Top Drop Zone"
              : "Bottom Drop Zone";
          case "left-column":
            return "Left Drop Zone";
          case "right-column":
            return "Right Drop Zone";
          default:
            return "Drop Zone";
        }
      }
      return "";
    };

    const getZoneDescription = () => {
      if (!isEmpty) return "";

      // Get current layout for description swapping
      const selectedLayoutId =
        fundraiserStore.builderData?.selectedLayout ||
        FundRaiserTemplateLayoutType.Layout1;

      switch (slotType) {
        case "full-width":
          return slotId.includes("top")
            ? "Ideal for Mission, Impact, Media and Progress"
            : "Ideal for Sponsor Ad, Description, Call to Action and Stats";
        case "left-column":
          // Swap descriptions for Layout 2
          if (selectedLayoutId === FundRaiserTemplateLayoutType.Layout2) {
            return "Ideal for Leaderboard, Donations, Call to Action, Progress and Sponsor Ad";
          }
          return "Ideal for Media, Donation Form, Stats Media and Progress";
        case "right-column":
          // Swap descriptions for Layout 2
          if (selectedLayoutId === FundRaiserTemplateLayoutType.Layout2) {
            return "Ideal for Media, Donation Form, Stats Media and Progress";
          }
          return "Ideal for Leaderboard, Donations, Call to Action, Progress and Sponsor Ad";
        default:
          return "";
      }
    };

    return (
      <DropZoneContainer
        ref={setNodeRef}
        zoneWidth={zoneWidth}
        isLayout5={
          selectedLayoutId === FundRaiserTemplateLayoutType.Layout5 &&
          slotType === "full-width"
        }
        isLayout4={isLayout4}
        layoutId={selectedLayoutId}
        slotId={slotId}
        isOver={isOver}
        isEmpty={isEmpty}
        slotType={slotType}
      >
        {isEmpty && (
          <>
            <DropZoneText isOver={isOver}>{getDropZoneText()}</DropZoneText>
            <ZoneDescription isOver={isOver} slotType={slotType}>
              {getZoneDescription()}
            </ZoneDescription>
          </>
        )}
        {!isEmpty && isOver && (
          <DropZoneText isOver={isOver}>{getDropZoneText()}</DropZoneText>
        )}
      </DropZoneContainer>
    );
  }
);
